import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useCourseData } from '../hooks/useCourseData';
import { useContentCourse } from '../context/ContentContext';
import api from '../../../../services/api';
import apiV2 from '../../../../services/apiV2';
import apiBackoffice from '../../../../services/apiBackoffice';
import { useToast } from '../../../../hooks/toast';
import { useAdminDelete } from '../../Admin/hooks/useAdminDelete';
import {
  ContentEditorLayout,
  Confirm,
  useContentEditor,
} from '../../../../components/Admin';
import Field from '../../../../components/Admin/Field';
import { TextInput, TextArea } from '../../../../components/Admin/Field/styles';
import OptionsEditor from './OptionsEditor';
import { parseOptions, stripOptionLetter, findCorrectIndex } from '../utils/options';

const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

interface ExerciseValues {
  question: string;
  shortMessage: string;
  options: string[];
  correctIndex: number;
}

const EMPTY: ExerciseValues = {
  question: '',
  shortMessage: 'Exercício de Fixação',
  options: ['', '', '', ''],
  correctIndex: 0,
};

interface ExerciseItem {
  exerciseid: string;
  question?: string;
  /** String JSON no backend (ex.: '["A) texto"]'), mas aceitamos array também. */
  options?: string | string[];
  answer?: string;
  position?: number;
  exerciseshortmessage?: string;
}

/** Busca os exercícios da aula (API principal, com fallback para a v2). */
const fetchExercises = async (
  courseid: string,
  seasonid: string,
  movieid: string,
): Promise<ExerciseItem[]> => {
  const path = `/course/season/movie/exercise?courseid=${courseid}&seasonid=${seasonid}&movieid=${movieid}&retry=false&userid=admin`;

  try {
    const response = await api.get<any>(path);
    if (Array.isArray(response.data)) return response.data;
    if (Array.isArray(response.data?.exercises)) return response.data.exercises;
  } catch {
    const response = await apiV2.get<any>(path);
    if (Array.isArray(response.data)) return response.data;
    if (Array.isArray(response.data?.exercises)) return response.data.exercises;
  }

  return [];
};

const ExerciseForm: React.FC = () => {
  const { addToast } = useToast();
  const { courseId } = useContentCourse();
  const {
    seasons,
    movies,
    loadingSeasons,
    loadingMovies,
    selectedCourseId,
    selectedSeasonId,
    selectedMovieId,
    handleSeasonChange,
    handleMovieChange,
  } = useCourseData(courseId);
  const { deleteItem, deletingId } = useAdminDelete();

  const { values, setField, selectedId, editing, dirty, select, clear, markSaved } =
    useContentEditor<ExerciseValues>(EMPTY);

  const [exercises, setExercises] = useState<ExerciseItem[]>([]);
  const [loadingExercises, setLoadingExercises] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const moduleOptions = useMemo(
    () =>
      seasons.map((season) => ({
        value: season.seasonid,
        label: season.title,
        hint: season.seasonid,
      })),
    [seasons],
  );

  const lessonOptions = useMemo(
    () =>
      movies.map((movie) => ({
        value: movie.movieid,
        label: movie.title,
        hint: `posição ${movie.position ?? '-'}`,
      })),
    [movies],
  );

  const loadExercises = useCallback(async () => {
    if (!selectedCourseId || !selectedSeasonId || !selectedMovieId) {
      setExercises([]);
      return;
    }

    setLoadingExercises(true);
    try {
      setExercises(await fetchExercises(selectedCourseId, selectedSeasonId, selectedMovieId));
    } finally {
      setLoadingExercises(false);
    }
  }, [selectedCourseId, selectedSeasonId, selectedMovieId]);

  useEffect(() => {
    loadExercises();
  }, [loadExercises]);

  const pickerItems = useMemo(
    () =>
      exercises.map((exercise) => ({
        id: exercise.exerciseid,
        label: exercise.question || `Exercício ${exercise.exerciseid}`,
        hint: `nº ${exercise.exerciseid}`,
      })),
    [exercises],
  );

  const onModuleChange = (seasonId: string) => {
    clear();
    handleSeasonChange(seasonId, selectedCourseId);
  };

  const onLessonChange = (movieId: string) => {
    clear();
    handleMovieChange(movieId);
  };

  const onSelectItem = (exerciseId: string) => {
    const exercise = exercises.find((item) => item.exerciseid === exerciseId);
    if (!exercise) return;

    // `options` chega como string JSON do backend — parseOptions aceita os dois formatos.
    const parsed = parseOptions(exercise.options).map(stripOptionLetter);
    const correctIndex = findCorrectIndex(parseOptions(exercise.options), exercise.answer);

    select(exerciseId, {
      question: exercise.question ?? '',
      shortMessage: exercise.exerciseshortmessage ?? 'Exercício de Fixação',
      options: parsed.length >= 2 ? parsed : ['', '', '', ''],
      correctIndex,
    });
  };

  const onChangeOption = (index: number, value: string) => {
    const next = [...values.options];
    next[index] = value;
    setField('options', next);
  };

  const onAddOption = () => {
    if (values.options.length < LETTERS.length) {
      setField('options', [...values.options, '']);
    }
  };

  const onRemoveOption = (index: number) => {
    if (values.options.length <= 2) {
      addToast({
        type: 'error',
        title: 'Atenção',
        description: 'Um exercício precisa de pelo menos 2 alternativas.',
      });
      return;
    }

    const next = values.options.filter((_, position) => position !== index);
    setField('options', next);
    if (values.correctIndex >= next.length) setField('correctIndex', 0);
  };

  const validate = () => {
    if (!selectedCourseId || !selectedSeasonId || !selectedMovieId) {
      addToast({
        type: 'error',
        title: 'Selecione módulo e aula',
        description: 'O exercício pertence a uma aula — escolha módulo e aula antes.',
      });
      return false;
    }
    if (!values.question.trim()) {
      addToast({ type: 'error', title: 'Enunciado obrigatório' });
      return false;
    }
    if (values.options.some((option) => !option.trim())) {
      addToast({
        type: 'error',
        title: 'Alternativas incompletas',
        description: 'Preencha o texto de todas as alternativas.',
      });
      return false;
    }
    return true;
  };

  const persist = async (exerciseId: string) => {
    const formattedOptions = values.options.map(
      (option, index) => `${LETTERS[index]}) ${option.trim()}`,
    );
    const position = Number(exerciseId) || 1;

    const body = {
      courseid_seasonid_movieid_exerciseid: `${selectedCourseId}_${selectedSeasonId}_${selectedMovieId}_${exerciseId}`,
      courseid: selectedCourseId,
      seasonid: selectedSeasonId,
      movieid: String(selectedMovieId),
      exerciseid: String(exerciseId),
      question: values.question.trim(),
      answer: formattedOptions[values.correctIndex],
      options: formattedOptions,
      position,
      questiontype: 'options',
      contenttype: 'normal',
      url: ' ',
      exerciseshortmessage: values.shortMessage.trim() || 'Exercício de Fixação',
    };

    setSaving(true);
    try {
      const response = await apiBackoffice.post('/backoffice/exercise/add', body);
      const ok =
        response.status === 200 || response.data === 'OK' || response.data?.message === 'OK';

      if (!ok) throw new Error('O servidor não confirmou a gravação.');

      addToast({
        type: 'success',
        title: editing ? 'Exercício atualizado' : 'Exercício cadastrado',
      });
      markSaved();
      await loadExercises();
      return true;
    } catch (err: any) {
      addToast({
        type: 'error',
        title: 'Erro ao salvar o exercício',
        description: err?.response?.data?.error || err?.message || 'Tente novamente.',
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const onSave = async () => {
    if (!validate()) return;

    if (editing) {
      await persist(selectedId);
      return;
    }

    const nextId =
      exercises.reduce((max, item) => Math.max(max, Number(item.exerciseid) || 0), 0) + 1;
    const ok = await persist(String(nextId));
    if (ok) clear();
  };

  const onDelete = async () => {
    setConfirmDelete(false);
    if (!selectedId) return;

    const done = await deleteItem({
      entity: 'exercise',
      id: `${selectedCourseId}_${selectedSeasonId}_${selectedMovieId}_${selectedId}`,
      successTitle: 'Exercício excluído',
    });

    if (done) {
      clear();
      await loadExercises();
    }
  };

  return (
    <>
      <ContentEditorLayout
        moduleOptions={moduleOptions}
        moduleId={selectedSeasonId}
        onModuleChange={onModuleChange}
        modulesLoading={loadingSeasons}
        lessonOptions={lessonOptions}
        lessonId={selectedMovieId}
        onLessonChange={onLessonChange}
        lessonsLoading={loadingMovies}
        pickerItems={pickerItems}
        pickerTitle="Exercícios desta aula"
        selectedItemId={selectedId}
        onSelectItem={onSelectItem}
        pickerLoading={loadingExercises}
        editing={editing}
        dirty={dirty}
        saving={saving}
        deleting={!!deletingId}
        onSave={onSave}
        onEdit={onSave}
        onDelete={() => setConfirmDelete(true)}
        onClear={clear}
      >
        <Field label="Enunciado da questão" required full>
          <TextArea
            value={values.question}
            onChange={(event) => setField('question', event.target.value)}
            placeholder="Ex.: Qual foi a principal característica dos Pais Apostólicos?"
          />
        </Field>

        <Field label="Título no menu do aluno" hint="Como o exercício aparece para o aluno">
          <TextInput
            value={values.shortMessage}
            onChange={(event) => setField('shortMessage', event.target.value)}
            placeholder="Exercício de Fixação"
          />
        </Field>
        <Field label="Alternativas" required full>
          <OptionsEditor
            name="exercise-correct-option"
            options={values.options}
            correctIndex={values.correctIndex}
            onChangeOption={onChangeOption}
            onChangeCorrect={(index) => setField('correctIndex', index)}
            onAdd={onAddOption}
            onRemove={onRemoveOption}
          />
        </Field>
      </ContentEditorLayout>

      <Confirm
        open={confirmDelete}
        danger
        title="Excluir exercício"
        description="O exercício some da aula para os alunos. Não dá para desfazer."
        confirmLabel="Excluir"
        loading={!!deletingId}
        onConfirm={onDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </>
  );
};

export default ExerciseForm;
